import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { FindDashboardItemDTO, FindGrafanaItem, FindGrafanaItemsDTO, FindItem, GrafanaTreeDTO, GrafanaTreeItem } from "@dto/dashboard.dto";
import { DashboardContextMenu, MenuDataNode } from "@parts/dashboard/select/DashboardContextMenu";
import { Button, Collapse, Divider, Space, TreeSelect } from "antd";
import { SafeKey } from "antd/es/table/interface";
import axios, { AxiosError } from "axios";
import React from "react";
import { FaPlus, FaTimes } from 'react-icons/fa';
import { DashTreeSourceNode, loadDashboardTree, mapDashTree } from 'src/component/dashboardTree/dashboardTreeSource';
import { XmModal } from "src/component/modal/XmModal";
import { getApi, postApi } from "src/util/apiCalls";
import { XmAsyncButton } from "../../../component/asyncButton/XmAsyncButton";
import { API_URL } from "../../../data/Api";
import { MenuNode } from "../../../model/MenuNode";
import { Log } from '../../../service/log.service';
import { GLOB } from "../../../util/Glob";
import "./AbstractDashboardSelect.less";

export interface CommonSelectProps {
  tabName?: string;
  subTabName?: string;
  tabId?: string;
  dashUrl?: string;
  dashboardItems?: FindItem[];
  setDashboardItems?: (fg: FindItem[]) => void;
}

export interface DashboardSelectProps {
  common: CommonSelectProps;
  timestamp: number;
}

export interface DashboardSelectState {
  selects: DashboardSelect[];
  grafanaSelects: DashboardSelect[];
  options: MenuDataNode[];
  grafanaOptions: MenuDataNode[];
  modalOpen: boolean;
  grafanaTrial: boolean;
  dashboardItems: FindItem[];
  grafanaItems: FindGrafanaItem[];
}

export class DashboardSelect {
  value: string;
  selected: MenuDataNode;
  dashId: string;
  open: boolean;
  id: string;
  key: number;
  expandedKeys: SafeKey[];
  isAllowed?: boolean;
}

enum PanelID {
  DASHBOARD = 'dashboard', GRAFANA = 'grafana'
}

export default abstract class AbstractDashboardSelect<P extends DashboardSelectProps = DashboardSelectProps, S extends DashboardSelectState = DashboardSelectState> extends React.Component<P, S> {

  static readonly GRAFANA_PREFIX = PanelID.GRAFANA + '-';

  private readonly contextMenu: DashboardContextMenu;
  private readonly abortCtrl = new AbortController();
  private selectCount = 0;
  protected operations: Record<string, () => Promise<unknown>> = {};
  protected grafanaLoaded = false;
  private activePanel = PanelID.DASHBOARD;
  readonly DASHBOARD = 'Dashboard';

  constructor(props: P) {
    super(props);
    this.state = {
      selects: [], grafanaSelects: [],
      options: [], grafanaOptions: [],
      modalOpen: false, grafanaTrial: false,
      dashboardItems: [], grafanaItems: []
    } as S;
    this.contextMenu = new DashboardContextMenu(() => this.state, this.setState.bind(this) as typeof this.setState.bind, (item) => item.dashboard,
      item => item.group, this.onDelete.bind(this) as typeof this.onDelete, null, true);
  }

  componentWillUnmount(): void {
    this.abortCtrl.abort();
  }

  componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot?: any): void {
    const { readonly } = GLOB.userInfo;
    if (this.props.timestamp && prevProps.timestamp !== this.props.timestamp) {
      this.grafanaLoaded = false;
      this.operations = {};

      const existing = this.props.common?.dashboardItems?.filter(fg => !this.props['interval'] || fg.interval === this.props['interval']);
      const selects: DashboardSelect[] = [];
      if (existing?.length)
        selects.push(...existing.map(fg => this.createSelect(fg)));
      if (!selects?.length) {
        selects.push(this.createSelect());
      }

      if (readonly) {
        const noGroupGraphs = new Set(
          this.props.common?.dashboardItems?.filter(fg => (fg.group_id ?? null) === null)
            .map(fg => fg.dashboard_item_id)
        );
        for (const select of selects) {
          if (select.dashId !== undefined) {
            select.isAllowed = noGroupGraphs.has(select.id);
          }
        }
      }

      this.setState({
        dashboardItems: [...(this.props.common?.dashboardItems || [])],
        options: [],
        modalOpen: true, selects,
        grafanaSelects: []
      });
      void this.loadDashboardOptions();
    }
  }

  protected abstract updateItem(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode, dashboardItems: S['dashboardItems'], path: string);

  protected abstract addItem(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode, dashboardItems: S['dashboardItems'], path: string);

  protected abstract addGrafana(ds: DashboardSelect, dash: MenuDataNode);

  protected abstract showItemInDashboardWarn(dash: MenuDataNode);

  protected abstract postToGrafana(dashboard: MenuDataNode): Promise<unknown>;

  protected abstract postToDashboard(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode): Promise<unknown>;

  protected abstract updateInDashboard(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode): Promise<unknown>;

  protected removeItem(ds: DashboardSelect, dashboardItems: S['dashboardItems']) {
    const idx = dashboardItems.findIndex(fg => fg.dashboard_item_id === ds.id);
    if (idx >= 0)
      dashboardItems.splice(idx, 1);
    else {
      const key = String(ds.key);
      const idxNew = dashboardItems.findIndex(fg => fg.dashboard_item_id === key);
      if (idxNew >= 0) {
        dashboardItems.splice(idxNew, 1);
      }
    }
  };

  protected createSelect(fg?: FindItem): DashboardSelect {
    return {
      open: false,
      expandedKeys: [DashboardContextMenu.ROOT],
      value: fg ? `${this.DASHBOARD}${fg.group_id == null ? '' : ('/' + GLOB.menuService.getDashMenu().children.find(mdn => mdn.group_id === fg.group_id)?.titleText)}${fg.path && fg.path !== '/' ? fg.path : ''}/${fg.dashboard.label}/${fg.dashboard_group.label}`
        : null,
      selected: fg ? {
        key: fg.dashboard_group.dashboard_group_id, value: fg.dashboard_group.dashboard_group_id, itemId: fg.dashboard_group.dashboard_group_id,
        titleText: fg.dashboard_group.label, isLeaf: true, dashboard: false, group: true
      } : null, key: this.selectCount++, id: fg?.dashboard_item_id, dashId: fg?.dashboard.dashboard_id
    };
  }

  private loadDashboardOptions() {
    const { readonly } = GLOB.userInfo;
    return loadDashboardTree(this.abortCtrl.signal).then(src => {
      const toNode = (n: DashTreeSourceNode, children: MenuDataNode[]): MenuDataNode => {
        const mdn: MenuDataNode = {
          key: n.id, value: n.id, itemId: n.id, titleText: n.label, label: null,
          isLeaf: n.type === 'group',
          dashboard: n.type === 'dashboard',
          group: n.type === 'group',
          disabled: readonly && n.groupId != null,
          ...(n.groupRoot ? { group_id: n.groupId } : {}),
        };
        if (n.type === 'dashboard' && !children.length) {
          children = [{
            disabled: true, label: 'No groups yet', key: n.id + DashboardContextMenu.NO_GROUP,
            value: n.id + DashboardContextMenu.NO_GROUP,
            itemId: DashboardContextMenu.NO_GROUP, group: true, isLeaf: true
          }];
        }
        if (children.length) {
          children.forEach(c => c.parent = mdn);
          mdn.children = children;
        }
        mdn.title = this.contextMenu.createLabel(mdn);
        return mdn;
      };
      const personal = mapDashTree(src.filter(n => n.type !== 'user_group'), toNode);
      const shared = mapDashTree(src.filter(n => n.type === 'user_group'), toNode);
      if (shared.length) {
        personal.push({
          itemId: MenuNode.GROUPS_DIVIDER_KEY, key: MenuNode.GROUPS_DIVIDER_KEY,
          value: MenuNode.GROUPS_DIVIDER_KEY, disabled: true, isLeaf: true, divider: true, titleText: '',
          title: <div>---------------------- Group Dashboards ---------------------</div>
        }, ...shared);
      }
      const root: MenuDataNode = {
        titleText: this.DASHBOARD, itemId: DashboardContextMenu.ROOT, key: DashboardContextMenu.ROOT,
        value: DashboardContextMenu.ROOT, isLeaf: false, children: personal
      };
      personal.forEach(c => c.parent = root);
      root.title = this.contextMenu.createLabel(root);
      this.setState(prev => ({
        options: [root],
        selects: prev.selects.map(ds => {
          if (!ds.selected?.value) return ds;
          const chain = this.findRekur(String(ds.selected.value), [root]);
          if (!chain) return ds;
          return { ...ds, expandedKeys: chain.slice(1).map(n => n.key as SafeKey) };
        })
      }));
    }, (reason: AxiosError) => Log.error('Failed to load dashboard tree!', reason));
  }

  static refreshRekur(item: MenuDataNode, nodes: MenuDataNode[]) {
    if (!item || !nodes)
      return;
    for (const node of nodes) {
      if (DashboardContextMenu.equals(item, node)) {
        if (item.children)
          item.children = [...item.children];
        return nodes.map(mdn => {
          if (DashboardContextMenu.equals(item, mdn)) {
            const itemNew = { ...item };
            if (item.children) {
              item.children.forEach(c => c.parent = itemNew);
            }
            return itemNew;
          }
          return mdn;
        });
      }
      const result = this.refreshRekur(item, node.children);
      if (result) {
        node.children = result;
        return [...nodes];
      }
    }
  }

  private onDelete(item: MenuDataNode) {
    for (const ds of this.state.selects) {
      if (ds.selected?.value === item.value || (ds.dashId != null && (ds.dashId === item.itemId || ds.dashId === item.value))) {
        ds.selected = null;
        ds.value = null;
        ds.id = null;
      }
    }
  }

  private createGrafanaSelect(fgg?: FindGrafanaItem): DashboardSelect {
    const uid = fgg?.dashboard.grafana_dashboard_uid;
    return {
      open: false, expandedKeys: [], value: fgg ? `Grafana / ${fgg.dashboard.label}` : null,
      selected: fgg ? {
        key: uid, value: uid, itemId: uid,
        titleText: fgg.dashboard.label, isLeaf: true, dashboard: true, group: false
      } : null, key: this.selectCount++, id: AbstractDashboardSelect.GRAFANA_PREFIX + fgg?.grafana_item_uid, dashId: uid
    };
  }

  private setOpen(open: boolean, dc: DashboardSelect) {
    dc.open = open;
    this.setState(prev => ({ selects: prev.selects.map(c => c === dc ? { ...c } : { ...c, open: false }) }));
  }

  private grafanaItemToDataNode(items: GrafanaTreeItem[]): MenuDataNode[] {
    if (!items) return null;
    return items.map(gti => {
      const id = AbstractDashboardSelect.GRAFANA_PREFIX + gti.uid;
      const isDashboard = gti.type === 'dashboard';
      const mdn: MenuDataNode = {
        ...gti, key: id, itemId: id, label: null, title: gti.label, titleText: gti.label, value: id, isLeaf: isDashboard,
        dashboard: isDashboard, children: this.grafanaItemToDataNode(gti.children)
      };
      mdn.title = <span>{this.contextMenu.getIconTitle(mdn)}</span>;
      return mdn;
    });
  }

  private removeSelect(ds: DashboardSelect) {
    if (ds.id) {
      this.removeItem(ds, this.state.dashboardItems);
      this.operations[ds.key] = () => axios.delete<BasicResponseDTO>(API_URL.DASHBOARD_ITEM + '/' + ds.id).then(response => {
        this.removeItem(ds, this.props.common.dashboardItems);
      }, (reason: AxiosError) => {
        Log.error('Failed to remove graph from dashboard!', reason);
      });

    } else {
      this.removeItem(ds, this.state.dashboardItems);
      delete this.operations[ds.key];
    }
    if (this.activePanel === PanelID.DASHBOARD)
      this.setState(prev => ({ selects: prev.selects.filter(c => c !== ds).map(c => ({ ...c, open: false })) }));
    else
      this.setState(prev => ({ grafanaSelects: prev.grafanaSelects.filter(c => c !== ds).map(c => ({ ...c, open: false })) }));
  }

  private addCascader() {
    if (this.activePanel === PanelID.DASHBOARD)
      this.setState(prev => ({ selects: [...prev.selects.map(c => ({ ...c, open: false })), this.createSelect()] }));
    else if (this.activePanel !== PanelID.GRAFANA)
      return;
    else if (!GLOB.grafanaActive)
      Log.warn('Grafana is not configured yet.');
    else if (this.state.grafanaTrial)
      Log.warn('Grafana functionality requires REST API license.');
    else if (!GLOB.userInfo.isAdmin)
      Log.warn('Only admins can export graphs to Grafana.');
    else
      this.setState(prev => ({ grafanaSelects: [...prev.grafanaSelects.map(c => ({ ...c, open: false })), this.createSelect()] }));
  }

  private findRekur(value: string, options: MenuDataNode[]): MenuDataNode[] {
    if (!value || !options) return;
    for (const node of options) {
      if (node.value === value) return [node];
      const result = this.findRekur(value, node.children);
      if (result) {
        result.push(node);
        return result;
      }
    }
  }

  private onChange(value: string, ds: DashboardSelect, options: MenuDataNode[]) {
    const found = this.findRekur(value, options);
    if (found?.[0].isLeaf) {
      const path = found.toReversed().filter((v, index) => index < found.length - 2).map(mdn => mdn.titleText).join('/');
      if (found[0].value.startsWith(AbstractDashboardSelect.GRAFANA_PREFIX)) {
        // no group in Grafana
        const dash = found[0];
        if (ds.id) {
          // should not happen
          return;
        } else {
          this.addGrafana(ds, dash);
          this.operations[ds.key] = () => this.postToGrafana(dash);
          ds.selected = dash;
        }
      } else {
        const group = found[0];
        const dash = found[1];

        if (ds.id) {
          if (this.showItemInDashboardWarn(dash)) {
            return;
          }
          this.updateItem(ds, dash, group, this.state.dashboardItems, path);
          this.operations[ds.key] = () => this.updateInDashboard(ds, dash, group);
        } else {
          this.removeItem(ds, this.state.dashboardItems);
          if (this.showItemInDashboardWarn(dash)) {
            return;
          }
          this.addItem(ds, dash, group, this.state.dashboardItems, path);
          this.operations[ds.key] = () => this.postToDashboard(ds, dash, group);
        }
        ds.selected = group;
      }
      ds.value = found.toReversed().map(mdn => mdn.titleText).join('/');
      this.setOpen(false, ds);
    }
  }

  private dropdownRender(menu: React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>) {
    return <div>
      {menu}
      <Divider style={{ margin: '12px 0' }} />
      <div style={{ margin: '0 0.5em' }}>Right click to create folder, dashboard or group.</div>
    </div>;
  }

  readonly isGrafana = (ds: DashboardSelect) => {
    return ds?.id?.startsWith(AbstractDashboardSelect.GRAFANA_PREFIX);
  };

  readonly onPanelActivate = (key: PanelID[]) => {
    this.activePanel = key[0];
    this.setState(prev => ({
      grafanaSelects: prev.grafanaSelects.map(c => ({ ...c, open: false })),
      selects: prev.selects.map(c => ({ ...c, open: false }))
    }));
    if (this.activePanel === PanelID.GRAFANA && !this.grafanaLoaded) {
      this.grafanaLoaded = true;
      if (GLOB.grafanaActive && GLOB.userInfo.isAdmin) {
        const grafanaSelects: DashboardSelect[] = [];
        const c = this.props.common;
        postApi<FindGrafanaItemsDTO, FindDashboardItemDTO>(API_URL.DASHBOARD + '/grafana/find',
          {
            tab: c.tabName, subtab: c.subTabName, tabId: c.tabId, url: c.dashUrl,
            interval: this.props['interval'], id: this.props['id']
          }).then(response => {
            const grafanaGraphs = response.data.data || [];
            if (grafanaGraphs?.length) {
              grafanaSelects.push(...grafanaGraphs.map(fgg => this.createGrafanaSelect(fgg)));
            }
            else {
              grafanaSelects.push(this.createSelect());
            }
            this.setState({ grafanaItems: grafanaGraphs, grafanaSelects });
          }, reason => {
            this.setState({ grafanaTrial: true });
            Log.error('Failed to find Grafana locations!', reason);
          });

        getApi<GrafanaTreeDTO>(API_URL.DASHBOARD_GRAFANA + '/tree', this.abortCtrl.signal).then(response => {
          const grafanaMenu = response.data.data;
          const opt: GrafanaTreeItem = {
            label: 'Grafana', uid: PanelID.GRAFANA as any,
            children: grafanaMenu, type: 'folder'
          };
          const grafanaOptions = this.grafanaItemToDataNode([opt]);
          this.setState({ grafanaOptions });
        }, reason => Log.error('Failed to get Grafana tree!', reason));
      }
    }
  };

  private isDashboard(): boolean {
    return true;// !!this.props.common.dashboardGraphs;
  }

  private onSave = () => {
    return Promise.all(Object.values(this.operations).map(o => o())).then(() => {
      setTimeout(() => {
        const dgs = [...(this.props.common.dashboardItems || [])];
        this.props.common.setDashboardItems?.(dgs);
        this.setState({ modalOpen: false, dashboardItems: dgs });
      });
    }, (reason) => console.log('Failed to update graph in dashboard!', reason));
  };

  render() {
    const { readonly } = GLOB.userInfo;
    return <XmModal
      open={this.state.modalOpen}
      destroyOnHidden={true}
      onOk={this.onSave}
      onCancel={() => this.setState({ modalOpen: false })}
      className="xm-abstract-dashboard-select-modal"
      footer={<div className="xm-abstract-dashboard-select-modal__footer">
        <Button
          title="Add another dashboard select"
          onClick={() => this.addCascader()}
          className="xm-abstract-dashboard-select-modal__add-button"
          icon={<FaPlus className="xm-abstract-dashboard-select-modal__add-icon" />}
        >
          Add entry
        </Button>
        <div className="xm-abstract-dashboard-select-modal__footer-actions">
          <Button onClick={() => this.setState({ modalOpen: false })}>Cancel</Button>
          <XmAsyncButton type="primary"
            onClickAsync={this.onSave}>Save</XmAsyncButton>
        </div>
      </div>}
    >
      <div className="xm-abstract-dashboard-select-modal__content">
        <Collapse bordered={false} accordion={true} defaultActiveKey={PanelID.DASHBOARD} onChange={this.onPanelActivate}>
          {this.isDashboard() && <Collapse.Panel key={PanelID.DASHBOARD} header="Show graph in dashboards">
            <Space orientation="vertical" className="xm-abstract-dashboard-select-modal__panel-content" onClick={e => {
              const el = e.target as HTMLElement;
              if (el.closest('.ant-tree-select,.ant-select-tree,.ant-dropdown-menu')) return;
              this.setState(prev => ({ selects: prev.selects.map(c => ({ ...c, open: false })) }));
            }}>
              {this.state.selects.map((ds, index) => <div key={ds.key} className="xm-abstract-dashboard-select-modal__row">
                <TreeSelect
                  className="xm-abstract-dashboard-select-modal__tree-select"
                  labelInValue
                  value={ds.selected?.value != null ? { value: ds.selected.value, label: ds.value } : null}
                  disabled={!(ds.isAllowed ?? true)}
                  classNames={{ popup: { root: "xm-dashboard-select" } }}
                  virtual={false}
                  popupRender={(menu) => this.dropdownRender(menu)}
                  placeholder="Select dashboard"
                  onChange={(v: { value: string; label?: React.ReactNode; }) => this.onChange(v?.value, ds, this.state.options)}
                  treeData={this.state.options}
                  open={this.state.selects.find(c => c.key === ds.key)?.open}
                  onClick={ev => (ev.target as Element).closest('.ant-tree-select') ? this.setOpen(!ds.open, ds) : null}
                  onSelect={(value, option) => {
                    const optKey = option.key as SafeKey;
                    if (ds.expandedKeys.includes(optKey)) {
                      ds.expandedKeys = ds.expandedKeys.filter(k => k !== optKey);
                    } else {
                      ds.expandedKeys = [...ds.expandedKeys, optKey];
                    }
                    this.setState(prev => ({ selects: [...prev.selects] }));
                  }}
                  onTreeExpand={exKeys => {
                    ds.expandedKeys = exKeys;
                    this.setState(prev => ({ selects: [...prev.selects] }));
                  }}
                  treeExpandedKeys={this.state.selects[index].expandedKeys}
                />
                <Button
                  type="text"
                  title="Remove from this dashboard"
                  className="xm-abstract-dashboard-select-modal__remove-button"
                  icon={<FaTimes />}
                  disabled={!(ds.isAllowed ?? true) || this.isGrafana(ds)}
                  onClick={() => this.removeSelect(ds)}
                />
              </div>)}
            </Space>
          </Collapse.Panel>}
          <Collapse.Panel key={PanelID.GRAFANA} header="Export graph to Grafana">
            <Space orientation="vertical" className="xm-abstract-dashboard-select-modal__panel-content" onClick={e => {
              const el = e.target as HTMLElement;
              if (el.closest('.ant-tree-select,.ant-select-tree,.ant-dropdown-menu')) return;
              this.setState(prev => ({ grafanaSelects: prev.grafanaSelects.map(c => ({ ...c, open: false })) }));
            }}>
              {this.state.grafanaSelects.map((ds, index) => <div key={ds.key} className="xm-abstract-dashboard-select-modal__row">
                <TreeSelect
                  className="xm-abstract-dashboard-select-modal__tree-select"
                  value={ds.value}
                  disabled={readonly || this.isGrafana(ds)}
                  classNames={{ popup: { root: "xm-dashboard-select" } }}
                  virtual={false}
                  placeholder="Select dashboard"
                  onChange={(value: string, labelList: React.ReactNode[]) => this.onChange(value, ds, this.state.grafanaOptions)}
                  showSearch={true}
                  treeData={this.state.grafanaOptions}
                  treeNodeFilterProp="titleText"
                  open={this.state.grafanaSelects.find(c => c.key === ds.key)?.open}
                  onClick={ev => (ev.target as Element).closest('.ant-tree-select') ? this.state.grafanaSelects.forEach(d => ds === d ? this.setOpen(!ds.open, ds) : this.setOpen(false, d)) : null}
                  //onSelect={(value, option) => {
                  // if (ds.expandedKeys.includes(option.key)) {
                  //   ds.expandedKeys = ds.expandedKeys.filter(k => k !== option.key);
                  // } else {
                  //   ds.expandedKeys = [...ds.expandedKeys, option.key];
                  // }
                  //   this.setState(prev => ({ grafanaSelects: [...prev.grafanaSelects] }));
                  // }}
                  treeDefaultExpandedKeys={[AbstractDashboardSelect.GRAFANA_PREFIX + PanelID.GRAFANA]}
                // onTreeExpand={exKeys => {
                //   ds.expandedKeys = exKeys;
                //   this.setState(prev => ({ grafanaSelects: [...prev.grafanaSelects] }));
                // }}
                //treeExpandedKeys={this.state.grafanaSelects[index].expandedKeys}
                />
                <Button
                  type="text"
                  title="Remove from this location"
                  className="xm-abstract-dashboard-select-modal__remove-button"
                  icon={<FaTimes />}
                  disabled={readonly || this.isGrafana(ds)}
                  onClick={() => this.removeSelect(ds)}
                />
              </div>)}
            </Space>
            <div className="xm-abstract-dashboard-select-modal__grafana-links">
              <a href="https://xormon.com/grafana.php" target="_blank">XorMon to Grafana documentation</a>
              {this.state.grafanaTrial && <a href="https://xormon.com/trial-form.php" target="_blank" className="xm-abstract-dashboard-select-modal__grafana-trial-link">Ask for XorMon REST API license</a>}
            </div>
          </Collapse.Panel>
        </Collapse>
      </div>
    </XmModal>;
  }
}
